export function formatCpfCnpj(val: string | null | undefined): string {
  if (!val) return '—';
  const digits = String(val).replace(/\D/g, '');
  if (digits.length === 14) {
    return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }
  if (digits.length === 11) {
    return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  }
  return digits;
}

export function sanitizeDocumento(val: string | null | undefined): string {
  return String(val ?? '').replace(/\D/g, '');
}

export function isCpfValido(val: string | null | undefined): boolean {
  const cpf = sanitizeDocumento(val);
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
  const digits = cpf.split('').map(Number);
  let soma = 0;
  for (let i = 0; i < 9; i++) soma += (digits[i] ?? 0) * (10 - i);
  let d1 = (soma * 10) % 11;
  if (d1 === 10) d1 = 0;
  if (d1 !== digits[9]) return false;
  soma = 0;
  for (let i = 0; i < 10; i++) soma += (digits[i] ?? 0) * (11 - i);
  let d2 = (soma * 10) % 11;
  if (d2 === 10) d2 = 0;
  return d2 === digits[10];
}

/** indIEDest da NF-e: 1=contribuinte ICMS (tem IE), 2=isento, 9=nao contribuinte. */
export type IndIEDest = '1' | '2' | '9';

export interface ClassificacaoIE {
  /** So os digitos da IE (vazio quando o texto nao tem numero). */
  digitos: string;
  /** indIEDest correspondente pra montar o <dest> da NF-e. */
  indIEDest: IndIEDest;
  /** true quando o texto e um estado reconhecido: numero, "ISENTO" ou vazio. */
  reconhecido: boolean;
}

/**
 * Classifica a Inscricao Estadual digitada pro destinatario da NF-e.
 *
 * Fecha o cStat 232 ("IE do destinatario nao informada"): antes qualquer texto
 * (ex.: "ISENTO") virava indIEDest=1 (contribuinte) mas o builder so emite a tag
 * <IE> quando ha digito -> nota "contribuinte SEM IE" que a SEFAZ recusa, e so na
 * hora de emitir. Aqui a base do indIEDest passa a ser o DIGITO:
 *  - tem numero            -> '1' (contribuinte, emite <IE>)
 *  - texto "ISENTO"        -> '2' (contribuinte isento, sem <IE>)
 *  - vazio                 -> '9' (nao contribuinte, sem <IE>)
 *  - texto solto sem numero-> '9' + reconhecido=false (fallback seguro + avisa no cadastro)
 */
export function classificarIE(val: string | null | undefined): ClassificacaoIE {
  const texto = String(val ?? '').trim();
  const digitos = texto.replace(/\D/g, '');
  if (digitos) return { digitos, indIEDest: '1', reconhecido: true };
  if (!texto) return { digitos: '', indIEDest: '9', reconhecido: true };
  const isento = texto.toUpperCase().includes('ISENT');
  return { digitos: '', indIEDest: isento ? '2' : '9', reconhecido: isento };
}

export function isCnpjValido(val: string | null | undefined): boolean {
  const cnpj = sanitizeDocumento(val);
  if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) return false;
  const digits = cnpj.split('').map(Number);
  const calc = (len: number, pesos: number[]): number => {
    let soma = 0;
    for (let i = 0; i < len; i++) soma += (digits[i] ?? 0) * (pesos[i] ?? 0);
    const r = soma % 11;
    return r < 2 ? 0 : 11 - r;
  };
  const p1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const p2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const d1 = calc(12, p1);
  digits[12] = digits[12] ?? d1;
  const d2 = calc(13, p2);
  return d1 === Number(cnpj[12]) && d2 === Number(cnpj[13]);
}
